import { useState } from 'react';
import { useQuery, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { SchedulerResult } from '../types';
import { useAuth } from '../contexts/AuthContext';
import './ScheduledScans.css';

export default function ScheduledScans() {
  const [triggering, setTriggering] = useState(false);
  const [lastResult, setLastResult] = useState<SchedulerResult | null>(null);
  const { user } = useAuth();

  const dueScans = useQuery(api.monitoring.getDueScans);
  const monitoringStats = useQuery(api.monitoring.getMonitoringStats);
  const schedulerStatus: any = useQuery(api.settings.getSetting, { key: 'schedulerStatus' });
  const schedulerLogs: any[] | undefined = useQuery(api.scheduler.getSchedulerLogs, { limit: 5 });

  const runNow = useAction(api.scheduler.runNow);

  // Show manual trigger only for specific admin users
  const showManualTrigger = user?.email === 'elie@anghami.com' || user?.email === 'elie@osnplus.com';

  const handleManualTrigger = async () => {
    setTriggering(true);
    setLastResult(null);

    try {
      const res = await runNow({});
      setLastResult({
        success: res?.success ?? false,
        message: res?.message || undefined,
        scansTriggered: res?.scansTriggered,
        timestamp: Date.now(),
      });
    } catch (error: any) {
      setLastResult({
        success: false,
        error: error.message,
        timestamp: Date.now(),
      });
    } finally {
      setTriggering(false);
    }
  };

  if (!dueScans || !monitoringStats) {
    return (
      <div className="scheduled-scans">
        <div className="loading">Loading scheduled scans...</div>
      </div>
    );
  }

  return (
    <div className="scheduled-scans">

      {/* Status Cards */}
      <div className="status-cards">
        <div className="status-card">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <div className="card-value">{monitoringStats.total}</div>
            <div className="card-label">Total Monitored</div>
          </div>
        </div>

        <div className="status-card">
          <div className="card-icon">✅</div>
          <div className="card-content">
            <div className="card-value">{monitoringStats.active}</div>
            <div className="card-label">Active Monitoring</div>
          </div>
        </div>

        <div className="status-card highlight">
          <div className="card-icon">🔔</div>
          <div className="card-content">
            <div className="card-value">{dueScans.length}</div>
            <div className="card-label">Due Now</div>
          </div>
        </div>

        <div className="status-card">
          <div className="card-icon">📅</div>
          <div className="card-content">
            <div className="card-value">{monitoringStats.dueWithin24h}</div>
            <div className="card-label">Due Within 24h</div>
          </div>
        </div>
      </div>

      {/* Cron Info Banner - Only for specific admin users */}
      {showManualTrigger && (
        <div className="cron-info-banner">
          <div className="banner-icon">ℹ️</div>
          <div className="banner-content">
            <h3>Automated Scheduling</h3>
            <p>
              Scans are automatically triggered jobs running <strong>every hour</strong>.
              You can also manually trigger scans below for testing purposes.
            </p>
            {schedulerStatus && (
              <p className="scheduler-last-run">
                <strong>Last run:</strong> {new Date(schedulerStatus.lastRunAt).toLocaleString()} ·
                <strong> Triggered:</strong> {schedulerStatus.scansTriggered}/{schedulerStatus.totalDue} ·
                <strong> Status:</strong> {schedulerStatus.success ? 'OK' : `Error${schedulerStatus.error ? ` (${schedulerStatus.error})` : ''}`}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Manual Trigger - Only for specific admin users */}
      {showManualTrigger && (
        <div className="manual-trigger-section">
          <h3>Manual Trigger (Test Mode)</h3>
          <p className="warning-text">
            ⚠️ <strong>Note:</strong> This is a TEST/DEMO feature. It simulates the scheduler check but does not trigger actual scans.
            Real scans are triggered automatically by the cron job every hour.
          </p>
          <p>
            This manual trigger reuses the same scheduler logic that production cron runs every hour. It activates once at least one monitoring item is due for a scan.
          </p>

          <button
            className="btn-trigger"
            onClick={handleManualTrigger}
            disabled={triggering || dueScans.length === 0}
          >
            {triggering ? (
              <>
                <span className="spinner">⏳</span> Testing Scheduler...
              </>
            ) : (
              <>
                <span>🧪</span> Test Scheduler Check
                {dueScans.length > 0 && ` (${dueScans.length} would be triggered)`}
              </>
            )}
          </button>

          {dueScans.length === 0 && (
            <div className="info-message">
              <span className="icon">ℹ️</span>
              No scans are currently due. All monitoring items are up to date.
            </div>
          )}

          {lastResult && (
            <div className={`result-message ${lastResult.success ? 'success' : 'error'}`}>
              <span className="icon">{lastResult.success ? '✅' : '❌'}</span>
              <div>
                <strong>{lastResult.success ? 'Success' : 'Error'}</strong>
                <p>{lastResult.message || lastResult.error}</p>
                {lastResult.scansTriggered !== undefined && (
                  <p className="detail">Triggered {lastResult.scansTriggered} scan(s)</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Scheduler Runs */}
      {schedulerLogs && schedulerLogs.length > 0 && (
        <div className="recent-runs">
          <h3>Recent Scheduler Runs</h3>
          <div className="runs-list">
            {schedulerLogs.map((log, idx) => (
              <div key={log._id || idx} className={`run-item ${log.success ? 'ok' : 'err'}`}>
                <span className="run-time">{new Date(log.createdAt).toLocaleString()}</span>
                <span className="run-count">Triggered {log.scansTriggered}/{log.totalDue}</span>
                <span className="run-status">{log.success ? 'OK' : 'Error'}</span>
                {!log.success && log.error && <span className="run-error">{log.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Due Scans List */}
      {dueScans.length > 0 && (
        <div className="due-scans-section">
          <h3>🔔 Scans Due Now ({dueScans.length})</h3>
          <div className="scans-list">
            {dueScans.map((scan) => (
              <div key={scan._id} className="scan-item">
                <div className="scan-header">
                  <div className="scan-title">{scan.contentTitle}</div>
                  <div className="scan-frequency">
                    <span className="frequency-badge">{scan.scanFrequency}</span>
                  </div>
                </div>

                <div className="scan-details">
                  {scan.priority && (
                    <span className={`detail-badge priority-${scan.priority}`}>
                      {scan.priority} priority
                    </span>
                  )}
                  {scan.contentType && (
                    <span className="detail-badge">{scan.contentType}</span>
                  )}
                  {scan.lastScanned && (
                    <span className="detail-text">
                      Last scanned: {new Date(scan.lastScanned).toLocaleString()}
                    </span>
                  )}
                </div>

                <div className="scan-stats">
                  {scan.totalScans !== undefined && (
                    <div className="stat">
                      <span className="stat-label">Total Scans:</span>
                      <span className="stat-value">{scan.totalScans}</span>
                    </div>
                  )}
                  {scan.averageViolations !== undefined && (
                    <div className="stat">
                      <span className="stat-label">Avg Violations:</span>
                      <span className="stat-value">{scan.averageViolations}</span>
                    </div>
                  )}
                  {scan.trendDirection && (
                    <div className="stat">
                      <span className="stat-label">Trend:</span>
                      <span className={`trend-badge trend-${scan.trendDirection}`}>
                        {scan.trendDirection === 'up' && '📈'}
                        {scan.trendDirection === 'down' && '📉'}
                        {scan.trendDirection === 'stable' && '➡️'}
                        {' '}{scan.trendDirection}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Scans */}
      <div className="upcoming-section">
        <h3>📅 Schedule Overview</h3>
        <p className="section-description">
          Scans are scheduled based on their frequency. The system checks every hour for due scans.
        </p>

        <div className="schedule-info">
          <div className="info-item">
            <span className="info-label">Daily Scans:</span>
            <span className="info-value">{monitoringStats.byFrequency.daily || 0} items</span>
          </div>
          <div className="info-item">
            <span className="info-label">Weekly Scans:</span>
            <span className="info-value">{monitoringStats.byFrequency.weekly || 0} items</span>
          </div>
          <div className="info-item">
            <span className="info-label">Monthly Scans:</span>
            <span className="info-value">{monitoringStats.byFrequency.monthly || 0} items</span>
          </div>
        </div>
      </div>
    </div>
  );
}
